import React, { Component } from 'react';
import PropTypes from 'prop-types';
import * as atlas from 'azure-maps-control';
import Spinner from "components/common/Spinner";
import 'azure-maps-control/dist/atlas.min.css';

class AzureMapComponent extends Component {
    constructor(props) {
        super(props);
        this.mapRef = React.createRef();
        this.map = null;
        this.dataSource = null;
        this.markers = []; // Store individual markers
        this.lineLayer = null; // Store line layer
        this.currentPopup = null; // Track current popup
    }

    componentDidMount() {
        this.initializeMap();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.mapData !== this.props.mapData) {
            this.updateMapData();
        }
    }

    componentWillUnmount() {
        if (this.map) {
            this.map.dispose();
        }
    }

    initializeMap = () => {
        // Initialize the map
        this.map = new atlas.Map(this.mapRef.current, {
            center: [-106.3, 54.5], // Center of Canada as default
            zoom: 4,
            language: 'en-US',
            authOptions: {
                authType: 'subscriptionKey',
                subscriptionKey: this.props.apiKey
            },
            style: 'road',
            showBringIntoViewControl: false,
            showLogo: false,
            showFeedbackLink: false
        });

        // Wait until the map resources are ready
        this.map.events.add('ready', () => {
            // Add Azure Map controls
            this.map.controls.add([
                new atlas.control.ZoomControl(),
                new atlas.control.PitchControl(),
                new atlas.control.CompassControl(),
                new atlas.control.StyleControl({
                    mapStyles: ['road', 'satellite', 'road_shaded_relief', 'grayscale_dark', 'grayscale_light', 'night', 'high_contrast_dark']
                })
            ], {
                position: 'top-right'
            });

            // Add fullscreen control to top-left
            this.map.controls.add(
                new atlas.control.FullscreenControl(),
                {
                    position: 'top-left'
                }
            );

            // Create data source for lines
            this.dataSource = new atlas.source.DataSource();
            this.map.sources.add(this.dataSource);

            // Create line layer
            this.lineLayer = new atlas.layer.LineLayer(this.dataSource, null, {
                strokeColor: '#666666',
                strokeWidth: 5,
                strokeOpacity: 1
            });
            this.map.layers.add(this.lineLayer);

            // Add click event to map to close popups when clicking elsewhere
            this.map.events.add('click', () => {
                this.closeFixedPopup();
            });

            // Initial data load
            this.updateMapData();
        });
    };

    updateMapData = () => {
        if (!this.map) {
            return;
        }

        // Clear existing markers and lines
        this.clearMarkers();
        this.clearLines();

        if (!this.props.mapData || this.props.mapData.length === 0) {
            return;
        }

        const coordinates = [];
        const lines = [];

        this.props.mapData.forEach((item, index) => {
            let storePosition = null;
            let employeePosition = null;

            // Add store marker
            if (item.storeMapModel && item.storeMapModel.latitude && item.storeMapModel.longitude) {
                storePosition = [
                    parseFloat(item.storeMapModel.longitude),
                    parseFloat(item.storeMapModel.latitude)
                ];

                const storeMarker = this.createMarker(
                    storePosition,
                    '🏪',
                    '#ff6b6b',
                    {
                        type: 'store',
                        title: item.storeMapModel.name || 'Store',
                        data: item.storeMapModel,
                        jobNumbers: item.jobNumbers || []
                    }
                );

                this.markers.push(storeMarker);
                coordinates.push(storePosition);
            }

            // Add employee marker
            if (item.employeeMapModel && item.employeeMapModel.latitude && item.employeeMapModel.longitude) {
                employeePosition = [
                    parseFloat(item.employeeMapModel.longitude),
                    parseFloat(item.employeeMapModel.latitude)
                ];

                const employeeMarker = this.createMarker(
                    employeePosition,
                    '👤',
                    '#4ecdc4',
                    {
                        type: 'employee',
                        title: item.employeeMapModel.name || `${item.employeeMapModel.firstName} ${item.employeeMapModel.lastName}`,
                        data: item.employeeMapModel,
                        jobNumbers: item.jobNumbers || []
                    }
                );

                this.markers.push(employeeMarker);
                coordinates.push(employeePosition);
            }

            // Create line between store and employee if both exist
            if (storePosition && employeePosition) {
                const lineString = new atlas.data.LineString([storePosition, employeePosition]);
                const lineFeature = new atlas.data.Feature(lineString, {
                    storeId: item.storeMapModel?.id,
                    employeeId: item.employeeMapModel?.id,
                    jobNumbers: item.jobNumbers || []
                });
                lines.push(lineFeature);
            }
        });

        // Add lines to data source
        if (lines.length > 0) {
            this.dataSource.add(lines);
        }

        // Fit map to show all markers with padding
        if (coordinates.length > 0) {
            const bounds = atlas.data.BoundingBox.fromPositions(coordinates);
            this.map.setCamera({
                bounds: bounds,
                padding: 50
            });
        } else {
            console.log('No coordinates to display');
        }
    };

    createMarker = (position, emoji, color, properties) => {
        // Create HTML marker with emoji
        const markerElement = document.createElement('div');
        markerElement.style.cssText = `
            background: white;
            border: 4px solid ${color};
            border-radius: 50%;
            width: 50px;
            height: 50px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 24px;
            cursor: pointer;
            box-shadow: 0 4px 8px rgba(0,0,0,0.3);
            z-index: 1000;
            user-select: none;
        `;
        markerElement.innerHTML = emoji;

        // Add hover effect without transform to avoid positioning issues
        markerElement.addEventListener('mouseenter', () => {
            markerElement.style.boxShadow = '0 6px 12px rgba(0,0,0,0.4)';
            markerElement.style.borderWidth = '5px';
        });
        markerElement.addEventListener('mouseleave', () => {
            markerElement.style.boxShadow = '0 4px 8px rgba(0,0,0,0.3)';
            markerElement.style.borderWidth = '4px';
        });

        // Create Azure Maps HTML marker FIRST
        const marker = new atlas.HtmlMarker({
            htmlContent: markerElement,
            position: position,
            anchor: 'center'
        });

        // Add click event to the marker element
        markerElement.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();

            console.log('Marker clicked:', properties.title);

            // Show popup in fixed position on map
            this.showFixedPopup(properties);
        });

        // Add marker to map
        this.map.markers.add(marker);

        return marker;
    };

    clearMarkers = () => {
        // Close any open popup
        this.closeFixedPopup();

        // Remove all existing markers
        if (this.markers.length > 0) {
            this.map.markers.remove(this.markers);
            this.markers = [];
        }
    };

    clearLines = () => {
        // Clear the data source which will remove all lines
        if (this.dataSource) {
            this.dataSource.clear();
        }
    };

    closeFixedPopup = () => {
        const existingPopup = this.mapRef.current?.querySelector('.fixed-map-popup');
        if (existingPopup) {
            existingPopup.remove();
        }
    };

    showFixedPopup = (properties) => {
        console.log('Creating fixed popup for:', properties.title);

        // Close any existing popup
        this.closeFixedPopup();

        // Create popup content
        const popupContent = this.createDetailedPopupContent(properties);

        // Create fixed popup container
        const popupContainer = document.createElement('div');
        popupContainer.className = 'fixed-map-popup';
        popupContainer.style.cssText = `
            position: absolute;
            top: 20px;
            right: 20px;
            background: white;
            border: none;
            border-radius: 12px;
            box-shadow: 0 8px 24px rgba(0,0,0,0.15);
            z-index: 10000;
            max-width: 380px;
            max-height: 400px;
            overflow-y: auto;
            animation: slideIn 0.3s ease-out;
        `;

        // Add close button
        const closeButton = document.createElement('button');
        closeButton.innerHTML = '×';
        closeButton.style.cssText = `
            position: absolute;
            top: 10px;
            right: 10px;
            background: #ff4444;
            color: white;
            border: none;
            border-radius: 50%;
            width: 28px;
            height: 28px;
            cursor: pointer;
            font-size: 18px;
            font-weight: bold;
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1;
            transition: background-color 0.2s;
        `;

        closeButton.addEventListener('mouseenter', () => {
            closeButton.style.background = '#3a3636';
        });
        closeButton.addEventListener('mouseleave', () => {
            closeButton.style.background = '#5e5c5c';
        });

        closeButton.addEventListener('click', () => {
            this.closeFixedPopup();
        });

        // Add content to container
        popupContainer.appendChild(popupContent);
        popupContainer.appendChild(closeButton);

        // Add popup to map container
        this.mapRef.current.appendChild(popupContainer);
    };

    createDetailedPopupContent = (properties) => {
        const isStore = properties.type === 'store';
        const data = properties.data;

        // Create a div element instead of HTML string for better reliability
        const popupDiv = document.createElement('div');
        popupDiv.style.cssText = `
            padding: 15px; 
            min-width: 280px; 
            font-family: Arial, sans-serif; 
            max-width: 350px;
            background: white;
            border-radius: 8px;
            box-shadow: 0 2px 10px rgba(0,0,0,0.1);
        `;

        popupDiv.innerHTML = `
            <div style="margin-bottom: 12px; border-bottom: 2px solid #eee; padding-bottom: 10px;">
                <div style="display: flex; align-items: center; margin-bottom: 5px;">
                    <span style="font-size: 24px; margin-right: 10px;">
                        ${isStore ? '🏪' : '👤'}
                    </span>
                    <strong style="color: #333; font-size: 18px;">${properties.title}</strong>
                </div>
            </div>
            
            <div style="margin-bottom: 15px;">
                <div style="margin-bottom: 8px; color: ${isStore ? '#ff6b6b' : '#4ecdc4'}; font-weight: bold; font-size: 16px;">
                    ${isStore ? '🏪 Store Information' : '👤 Employee Information'}
                </div>
                
                ${isStore ? `
                    <div style="margin-bottom: 6px; padding: 4px 0;"><strong>Store ID:</strong> ${data.id}</div>
                    <div style="margin-bottom: 6px; padding: 4px 0;"><strong>Name:</strong> ${data.name || 'N/A'}</div>
                    <div style="margin-bottom: 6px; padding: 4px 0;"><strong>Location:</strong> ${data.geoLocation || 'N/A'}</div>
                    <div style="margin-bottom: 6px; padding: 4px 0;"><strong>Coordinates:</strong> ${data.latitude.toFixed(6)}, ${data.longitude.toFixed(6)}</div>
                ` : `
                    <div style="margin-bottom: 6px; padding: 4px 0;"><strong>Employee ID:</strong> ${data.id}</div>
                    <div style="margin-bottom: 6px; padding: 4px 0;"><strong>Full Name:</strong> ${data.firstName} ${data.lastName}</div>
                    <div style="margin-bottom: 6px; padding: 4px 0;"><strong>Email:</strong> ${data.email}</div>
                    <div style="margin-bottom: 6px; padding: 4px 0;"><strong>Display Name:</strong> ${data.name || 'N/A'}</div>
                    <div style="margin-bottom: 6px; padding: 4px 0;"><strong>Location:</strong> ${data.geoLocation || 'N/A'}</div>
                    <div style="margin-bottom: 6px; padding: 4px 0;"><strong>Coordinates:</strong> ${data.latitude.toFixed(6)}, ${data.longitude.toFixed(6)}</div>
                `}
            </div>
            
            ${properties.jobNumbers && properties.jobNumbers.length > 0 ? `
                <div style="margin-top: 15px; padding-top: 12px; border-top: 2px solid #eee;">
                    <div style="margin-bottom: 8px; font-weight: bold; color: #666;">📋 Job Numbers:</div>
                    <div style="background: #f8f9fa; padding: 10px; border-radius: 6px; font-size: 14px; border-left: 4px solid #007acc;">
                        ${properties.jobNumbers.join(', ')}
                    </div>
                </div>
            ` : `
                <div style="margin-top: 15px; padding-top: 12px; border-top: 2px solid #eee; color: #999; font-style: italic;">
                    No job numbers assigned
                </div>
            `}
        `;

        return popupDiv;
    };

    render() {
        return (
            <div style={{ position: 'relative', width: '100%', height: '500px' }}>
                <div
                    ref={this.mapRef}
                    style={{
                        width: '100%',
                        height: '100%',
                        borderRadius: '8px',
                        border: '1px solid #ddd'
                    }}
                />

                {/* Loading overlay */}
                {this.props.loading && (
                    <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '8px',
                        zIndex: 1000
                    }}>
                        <Spinner/>
                    </div>
                )}

                {/* Empty state */}
                {(!this.props.mapData || this.props.mapData.length === 0) && !this.props.loading && (
                    <div style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        textAlign: 'center',
                        color: '#666',
                        zIndex: 1000
                    }}>
                        <div style={{ fontSize: '48px', marginBottom: '10px', opacity: 0.5 }}>🗺️</div>
                        <div style={{ fontSize: '18px', marginBottom: '5px' }}>No locations to display</div>
                        <div style={{ fontSize: '14px' }}>
                            Apply filters and search to see store and employee locations
                        </div>
                    </div>
                )}

                {/* Add CSS for popup animation */}
                <style jsx>{`
                    @keyframes slideIn {
                        from {
                            opacity: 0;
                            transform: translateX(20px);
                        }
                        to {
                            opacity: 1;
                            transform: translateX(0);
                        }
                    }
                    
                    .fixed-map-popup::-webkit-scrollbar {
                        width: 6px;
                    }
                    
                    .fixed-map-popup::-webkit-scrollbar-track {
                        background: #f1f1f1;
                        border-radius: 3px;
                    }
                    
                    .fixed-map-popup::-webkit-scrollbar-thumb {
                        background: #888;
                        border-radius: 3px;
                    }
                    
                    .fixed-map-popup::-webkit-scrollbar-thumb:hover {
                        background: #555;
                    }
                `}</style>
            </div>
        );
    }
}

AzureMapComponent.propTypes = {
    apiKey: PropTypes.string.isRequired,
    mapData: PropTypes.array,
    loading: PropTypes.bool
};

AzureMapComponent.defaultProps = {
    mapData: [],
    loading: false
};

export default AzureMapComponent;
